import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowPathIcon,
  PlusIcon,
  CalendarIcon,
  ListBulletIcon,
  UserGroupIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { useProject } from '../../contexts/ProjectContext';
import { getStatusLabel, getStatusColorClasses } from '../../utils/statusConfig';
import CreateProjectModal from './CreateProjectModal';

const statusFilters = [
  { id: 'all', label: 'Todos' },
  { id: 'pending', label: 'Pendiente' },
  { id: 'progress', label: 'En Progreso' },
  { id: 'completed', label: 'Completado' },
  { id: 'closed', label: 'Cerrado' }
];

const formatDate = (dateString) => {
  if (!dateString) return 'Sin fecha';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Sin fecha';
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getTagChipStyles = (color) => {
  const hex = color?.startsWith('#') ? color : `#${color || '4B5563'}`;
  return {
    backgroundColor: `${hex}20`,
    color: hex
  };
};

const ProjectCard = ({ project }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-200"
  >
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {project.name}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
          {project.description}
        </p>
      </div>
      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColorClasses(project.status)}`}>
        {getStatusLabel(project.status)}
      </span>
    </div>

    {project.tags && project.tags.length > 0 && (
      <div className="mb-4">
        <div className="flex items-center text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
          <TagIcon className="h-4 w-4 mr-1" />
          Etiquetas
        </div>
        <div className="flex flex-wrap gap-2">
          {project.tags.map((tag) => (
            <span
              key={`${project.id}-tag-${tag.id}`}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              style={getTagChipStyles(tag.color)}
            >
              {tag.name}
            </span>
          ))}
        </div>
      </div>
    )}

    {project.participants && project.participants.length > 0 && (
      <div className="mb-4">
        <div className="flex items-center text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
          <UserGroupIcon className="h-4 w-4 mr-1" />
          Participantes ({project.participants.length})
        </div>
        <div className="flex flex-wrap gap-2">
          {project.participants.map((participant) => (
            <div
              key={`${project.id}-participant-${participant.id}`}
              className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-xs text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
              title={participant.email}
            >
              <span className="font-medium">
                {participant.name} {participant.last_name}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}

    <div className="grid grid-cols-3 gap-3 text-xs text-gray-500 dark:text-gray-400 mt-4">
      <div className="flex items-center space-x-1">
        <ListBulletIcon className="h-4 w-4" />
        <span>{project.tasksCount} tareas</span>
      </div>
      <div className="flex items-center space-x-1">
        <CalendarIcon className="h-4 w-4" />
        <span>Inicio {formatDate(project.initialDate)}</span>
      </div>
      <div className="flex items-center space-x-1">
        <CalendarIcon className="h-4 w-4" />
        <span>Fin {formatDate(project.finalDate)}</span>
      </div>
    </div>

    <div className="mt-4">
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
        <span>Progreso</span>
        <span className="font-medium text-gray-900 dark:text-white">{project.progress || 0}%</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className="bg-primary-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${project.progress || 0}%` }}
        />
      </div>
    </div>
  </motion.div>
);

const ProjectManagement = () => {
  const { projects, loading, refreshProjects } = useProject();
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const filteredProjects = useMemo(() => {
    if (statusFilter === 'all') return projects;
    return projects.filter(
      (project) => (project.status || '').toLowerCase() === statusFilter
    );
  }, [projects, statusFilter]);

  const metrics = useMemo(() => {
    const total = projects.length;
    const completed = projects.filter(
      (project) => (project.status || '').toLowerCase() === 'completed'
    ).length;
    const inProgress = projects.filter(
      (project) => (project.status || '').toLowerCase() === 'progress'
    ).length;
    const avgProgress = total > 0
      ? Math.round(projects.reduce((acc, project) => acc + (project.progress || 0), 0) / total)
      : 0;

    return {
      total,
      completed,
      inProgress,
      avgProgress
    };
  }, [projects]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Gestión de Proyectos
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Visualiza el estado de todos tus proyectos, incluyendo las etiquetas y participantes asignados.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={refreshProjects}
              disabled={loading}
              className="btn-secondary flex items-center gap-2 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Actualizando' : 'Actualizar'}
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="btn-primary flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Nuevo Proyecto
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Proyectos</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{metrics.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">En Progreso</p>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{metrics.inProgress}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Completados</p>
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">{metrics.completed}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Progreso Promedio</p>
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{metrics.avgProgress}%</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {statusFilters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setStatusFilter(filter.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                statusFilter === filter.id
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="popLayout">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="card p-10 text-center text-gray-500 dark:text-gray-400">
              <p>No hay proyectos para este filtro.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onProjectCreated={refreshProjects}
      />
    </div>
  );
};

export default ProjectManagement;
